import React from 'react';
import { State } from '..';
import { INotificationContext } from 'utils/NotificationManager';
import { KeysOfType } from 'component_utils/typescriptUtils';
import Translation from 'utils/Language/Translation';
import { IBatch, IBinLocation, IItem, IItemBinLocation } from 'interfaces';
import { distinctBy } from 'component_utils/utils';
import { compoundItemBarcodeParser } from 'utils/Config';

interface EventProps {
  notifications: INotificationContext;
}

interface Config<S, P, A, R, T> {
  nextState: number;
  locationKey: KeysOfType<S, IBinLocation>;
  contentsKey: KeysOfType<S, IItemBinLocation[]>;
  itemKey: KeysOfType<S, IItem>;
  batchKey: KeysOfType<S, IBatch>;
  attributes?: Partial<A>;
}

const StateBatch = function <S, P extends EventProps, A extends { hint: string }, R, T>({
  nextState,

  locationKey,
  contentsKey,
  itemKey,
  batchKey,

  attributes = {},
}: Config<S, P, A, R, T>): State<S, P, A, R> {
  return {
    onEnter: async (args) => {
      const location = args.state[locationKey] as unknown as IBinLocation
      const contents = args.state[contentsKey] as unknown as IItemBinLocation[];
      const item = args.state[itemKey] as unknown as IItem;

      // liquid containers do not need to scan batches
      if (location.physicalLocationType === 'LIQUID_CONTAINER') {
        return {
          type: 'MOVE',
          data: {} as any,
          nextState,
        };
      }

      const batches = distinctBy(
        contents.filter((it) => it.item.code === item.code).map(it => it.batch),
        (it) => it.visualCode
      );
      if (batches.length === 1) {
        return {
          type: 'MOVE',
          data: {
            [batchKey]: batches[0],
          } as any,
          nextState,
        };
      }
  },
    onAction: async (args) => {
      const input = args.inputs[0];
      const contents = args.state[contentsKey] as unknown as IItemBinLocation[];
      const item = args.state[itemKey] as unknown as IItem;
      const { batchCode } = await compoundItemBarcodeParser(input, () => [item], 'batchCode')
      const batch = contents.find(
        content => content.itemCode === item.code && content.batch?.visualCode === batchCode,
      )?.batch;
      if (!batch) {
        throw args.props.notifications.add('danger', <Translation name="T.errors.unknownBatchCode" />);
      }
      return {
        type: 'MOVE',
        data: {
          [batchKey]: batch,
        } as any,
        nextState,
      };
    },
    attributes: {
      hint: 'T.hints.batch',
      ...(attributes as any),
    },

    nodeName: 'T.states.batch',
    outputs: [
      {
        name: 'batch',
        to: nextState,
      },
    ],
  };
};

export default StateBatch;
